import { Droppable } from '@hello-pangea/dnd';
import { Box, Stack, Typography, useTheme, Paper } from '@mui/material';
import { useRedirect, useTranslate } from 'react-admin';
import { VariableSizeList, areEqual } from 'react-window';
import { Draggable } from '@hello-pangea/dnd';
import { JobApplication, JobApplicationCardContent, JobApplicationCard } from './JobApplicationCard';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';

/* eslint-disable react/prop-types */

// Threshold for enabling virtualization - only virtualize columns with many items
const VIRTUALIZATION_THRESHOLD = 30;

// Estimated height used until we measure a specific card.
const ESTIMATED_ITEM_HEIGHT = 220;

// Extra space after the last card so the kanban horizontal scrollbar (often overlay on macOS)
// doesn't visually overlap the last card.
// Implemented as padding on the LAST rendered item (react-window scrollHeight comes from item sizes).
const KANBAN_LAST_ITEM_PADDING = 300;

type OuterElementContextValue = {
  droppableProps: Record<string, unknown>;
  backgroundColor: string;
};

const OuterElementContext = React.createContext<OuterElementContextValue | null>(null);

// IMPORTANT: must be stable (not recreated per render) or react-window will remount and reset scroll.
const OuterElement = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((props, ref) => {
  const ctx = React.useContext(OuterElementContext);

  return (
    <div
      {...props}
      {...(ctx?.droppableProps ?? {})}
      ref={ref}
      data-testid="job-application-column-react-window-outer"
      // eslint-disable-next-line react/prop-types
      style={{
        ...(props.style || {}),
        backgroundColor: ctx?.backgroundColor,
        transition: 'background-color 0.2s ease',
        overflowX: 'hidden',
      }}
    />
  );
});
OuterElement.displayName = 'JobApplicationColumnOuterElement';

type ItemData = {
  items: JobApplication[];
  onCardClick: (id: string | number) => void;
  setItemSize: (index: number, id: string | number, size: number) => void;
};

type RowProps = {
  data: ItemData;
  index: number;
  style: React.CSSProperties;
};

const getRowStyle = (args: {
  draggableStyle: React.CSSProperties | undefined;
  virtualStyle: React.CSSProperties;
}): React.CSSProperties => {
  const { draggableStyle, virtualStyle } = args;

  // react-window gives us the correct positional styles. During drag, DnD needs to win.
  const combined: React.CSSProperties = {
    ...virtualStyle,
    ...(draggableStyle || {}),
  };

  const grid = 8;
  return {
    ...combined,
    paddingLeft: grid,
    paddingRight: grid,
    paddingTop: 4,
    paddingBottom: 4,
    boxSizing: 'border-box',
    // keep itemSize consistent; we provide visual spacing via padding
    height: combined.height,
  };
};

const Row = React.memo((props: RowProps) => {
  // eslint-disable-next-line react/prop-types
  const { data, index, style } = props;
  const application = data.items[index];

  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!application) return;
    const el = contentRef.current;
    if (!el) return;

    const id = application.id;

    const measure = () => {
      // include vertical padding we add in getRowStyle (top+bottom = 8)
      const next = Math.ceil(el.getBoundingClientRect().height) + 8;
      data.setItemSize(index, id, next);
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [application?.id, application, data, index]);

  // Placeholder row for DnD (extra item to stand in for droppable placeholder)
  if (!application) {
    return null;
  }

  return (
    <Draggable draggableId={String(application.id)} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={getRowStyle({
            draggableStyle: provided.draggableProps.style as React.CSSProperties | undefined,
            virtualStyle: style as React.CSSProperties,
          })}
        >
          <div
            ref={contentRef}
            style={{
              // Reserve space after the last item so it can scroll above the horizontal scrollbar overlay.
              paddingBottom: index === data.items.length - 1 ? KANBAN_LAST_ITEM_PADDING : 0,
            }}
          >
            <JobApplicationCardContent
              application={application}
              isDragging={snapshot.isDragging}
              onClick={snapshot.isDragging ? undefined : () => data.onCardClick(application.id)}
            />
          </div>
        </div>
      )}
    </Draggable>
  );
}, areEqual);
Row.displayName = 'JobApplicationColumnRow';

export const JobApplicationColumn = ({
  statusId,
  applications,
  label,
}: {
  statusId: string;
  applications: JobApplication[];
  label: string;
}) => {
  const theme = useTheme();
  const translate = useTranslate();
  const redirect = useRedirect();
  
  // Only use virtualization for columns with many applications
  const shouldVirtualize = applications.length > VIRTUALIZATION_THRESHOLD;

  const onCardClick = useCallback(
    (id: string | number) => {
      redirect(`/job-application/${id}`);
    },
    [redirect]
  );

  type VariableSizeListHandle = { resetAfterIndex: (index: number, shouldForceUpdate?: boolean) => void };
  const listRef = useRef<VariableSizeListHandle | null>(null);
  const sizeMapRef = useRef<Map<string, number>>(new Map());

  const setItemSize = useCallback((index: number, id: string | number, size: number) => {
    const key = String(id);
    const prev = sizeMapRef.current.get(key);
    if (prev === size) return;
    sizeMapRef.current.set(key, size);
    // Recompute positions from this index onward
    listRef.current?.resetAfterIndex(index);
  }, []);

  const itemData: ItemData = useMemo(
    () => ({ items: applications, onCardClick, setItemSize }),
    [applications, onCardClick, setItemSize]
  );

  const listContainerRef = useRef<HTMLDivElement | null>(null);
  // Must always render a droppable scroll container in virtual mode.
  // Start with a minimal height so react-window mounts immediately and provided.innerRef receives an element.
  const [listHeight, setListHeight] = useState<number>(1);
  const scrollOffsetRef = useRef<number>(0);

  useEffect(() => {
    if (!shouldVirtualize) return;
    const el = listContainerRef.current;
    if (!el) return;

    const measure = () => {
      const next = el.clientHeight;
      const safeNext = Math.max(1, next);
      setListHeight((prev) => (prev === safeNext ? prev : safeNext));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [shouldVirtualize]);

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: '300px',
        maxWidth: '300px',
        // Keep columns visually consistent even with few cards
        // and enable internal vertical scrolling for long lists.
        height: 'calc(100vh - 280px)',
        minHeight: 'calc(100vh - 280px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.mode === 'dark' 
          ? theme.palette.grey[900] 
          : theme.palette.grey[50],
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,        
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          padding: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.mode === 'dark' 
            ? theme.palette.grey[800] 
            : theme.palette.grey[100],
        }}
      >
        <Stack alignItems="center" spacing={0.5}>
          <Typography 
            variant="subtitle1" 
            fontWeight={600}
            color={theme.palette.text.primary}
            sx={{ textAlign: 'center' }}
          >
            {label}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              padding: '2px 8px',
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 500,
            }}
          >
            {applications.length} {translate('resources.job-application.name', 2)}
          </Typography>
        </Stack>
      </Box>

      <Droppable
        droppableId={statusId}
        mode={shouldVirtualize ? 'virtual' : 'standard'}
        renderClone={
          shouldVirtualize
            ? (provided, snapshot, rubric) => {
                const application = applications[rubric.source.index];
                if (!application) return null;
                return (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={provided.draggableProps.style}
                  >
                    <JobApplicationCardContent application={application} isDragging={snapshot.isDragging} />
                  </div>
                );
              }
            : undefined
        }
      >
        {(provided, snapshot) => {
          // In virtual mode, placeholder must be emulated by rendering one extra empty row.
          const placeholderCount = snapshot.isUsingPlaceholder ? 1 : 0;
          const itemCount = applications.length + placeholderCount;

          if (shouldVirtualize) {
            return (
              <Box
                ref={listContainerRef}
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  overflow: 'hidden',
                  overflowX: 'hidden',
                  position: 'relative',
                }}
              >
                <OuterElementContext.Provider
                  value={{
                    droppableProps: provided.droppableProps as unknown as Record<string, unknown>,
                    backgroundColor: snapshot.isDraggingOver ? theme.palette.action.hover : 'transparent',
                  }}
                >
                  <VariableSizeList
                    ref={(node: unknown) => {
                      listRef.current = node as unknown as VariableSizeListHandle | null;
                    }}
                    height={listHeight}
                    itemCount={itemCount}
                    itemSize={(index: number) => {
                      const app = applications[index];
                      // placeholder row (extra item)
                      if (!app) return ESTIMATED_ITEM_HEIGHT;
                      return sizeMapRef.current.get(String(app.id)) ?? ESTIMATED_ITEM_HEIGHT;
                    }}
                    width="100%"
                    itemData={itemData}
                    outerRef={provided.innerRef}
                    outerElementType={OuterElement as unknown as React.ComponentType<React.HTMLAttributes<HTMLDivElement>>}
                    // hello-pangea/dnd requires overscanning of 1+ items for virtual lists
                    overscanCount={5}
                    // Safety net: if the list ever remounts, restore scroll
                    initialScrollOffset={scrollOffsetRef.current}
                    onScroll={({ scrollOffset }: { scrollOffset: number }) => {
                      scrollOffsetRef.current = scrollOffset;
                    }}
                  >
                    {Row}
                  </VariableSizeList>
                </OuterElementContext.Provider>
              </Box>
            );
          }

          return (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'auto',
                overflowX: 'hidden',
                backgroundColor: snapshot.isDraggingOver ? theme.palette.action.hover : 'transparent',
                transition: 'background-color 0.2s ease',
                paddingY: 1,
                position: 'relative',
              }}
            >
              {applications.length > 0 ? (
                <Box>
                  {applications.map((application, index) => (
                    <Box key={application.id} sx={{ paddingX: '12px', paddingBottom: 1.5 }}>
                      <JobApplicationCard application={application} index={index} />
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ paddingY: 1 }} />
              )}
              {provided.placeholder}
            </Box>
          );
        }}
      </Droppable>
    </Paper>
  );
};
